import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Home } from 'lucide-react';
import { useTopMenus, type TopMenuEntry } from './topMenus';
import { useAgentCard } from './AgentDock';

// The menu drawer, shown when the top bar's menus are collapsed: Home, then
// the top menus as expandable groups in one vertical list. A business area's
// subsections are reached from the breadcrumbs on its page, as on desktop.

function MobileItem({ entry: e, onNavigate }: {
  entry: Extract<TopMenuEntry, { kind: 'item' }>; onNavigate: () => void;
}) {
  const className = `w-full flex items-center gap-2.5 pr-4 py-2.5 text-left text-sm text-graphite hover:bg-offwhite ${e.muted ? 'opacity-50' : ''}`;
  const style = { paddingLeft: e.depth ? 48 : 28 };
  const select = () => { e.onSelect?.(); onNavigate(); };
  const body = (
    <>
      {e.icon && <span className="shrink-0 flex">{e.icon}</span>}
      <span className="min-w-0">{e.label}</span>
      {e.hint && <span className="ml-auto shrink-0">{e.hint}</span>}
    </>
  );
  return e.to ? (
    <Link to={e.to} onClick={select} title={e.title} style={style} className={className}>{body}</Link>
  ) : (
    <button type="button" onClick={select} title={e.title} style={style} className={className}>{body}</button>
  );
}

export default function MobileMenu({ onNavigate }: { onNavigate: () => void }) {
  const menus = useTopMenus();
  const { resetCard } = useAgentCard();

  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setOpen(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <nav aria-label="Menu" className="py-2">
      <div>
        <Link
          to="/"
          onClick={() => { resetCard(); onNavigate(); }}
          className="w-full h-11 flex items-center gap-2.5 pl-4 text-sm font-semibold text-graphite hover:bg-offwhite"
        >
          <Home className="w-4 h-4 shrink-0 text-coolslate" />
          Home
        </Link>
        {menus.map(group => {
          const key = `menu:${group.label}`;
          const isOpen = !!open[key];
          return (
            <div key={group.label}>
              <button
                type="button"
                onClick={() => toggle(key)}
                aria-expanded={isOpen}
                className="w-full flex items-center pl-4 text-sm font-semibold text-graphite"
              >
                <span className="flex-1 text-left">{group.label}</span>
                <span className="w-11 h-11 grid place-items-center text-coolslate">
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {isOpen && (
                <div className="pb-2">
                  {group.entries.map((e, i) =>
                    e.kind === 'divider' ? (
                      <div key={`d${i}`} className="my-1 mx-4 border-t border-softgrey" />
                    ) : (
                      <MobileItem key={e.key ?? e.to} entry={e} onNavigate={onNavigate} />
                    ),
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

    </nav>
  );
}
